import type { Message } from '@conversia/shared-types';

/**
 * Timeline del chat — orden, hora y autoría. PURO.
 *
 * INCIDENTE 2026-09-01: el panel ordenaba y mostraba por `createdAt`, que es la
 * hora de INGESTA, no la de envío. Un mensaje entregado con retraso mostraba la
 * hora equivocada. Además no había separador de día (un mensaje de ayer arriba
 * de los de hoy parecía un bug de orden) ni forma de saber quién había escrito
 * un saliente.
 */

/** Hora REAL del mensaje: la de envío si existe, si no la de ingesta. */
export function horaDeMensaje(m: Message): string {
  return m.sentAt ?? m.createdAt;
}

/** Ordena por hora real, de más viejo a más nuevo. No muta la entrada. */
export function ordenarMensajes(mensajes: Message[]): Message[] {
  return [...mensajes].sort(
    (a, b) => new Date(horaDeMensaje(a)).getTime() - new Date(horaDeMensaje(b)).getTime(),
  );
}

export interface GrupoDia {
  /** Clave estable del día (YYYY-MM-DD local). */
  dia: string;
  /** Texto visible: "Hoy", "Ayer" o la fecha larga. */
  etiqueta: string;
  mensajes: Message[];
}

/** Clave local del día — no UTC, para que el corte sea el del usuario. */
function claveDia(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function etiquetaDia(iso: string): string {
  const d = new Date(iso);
  const hoy = new Date();
  const ayer = new Date(hoy);
  ayer.setDate(hoy.getDate() - 1);
  if (claveDia(iso) === claveDia(hoy.toISOString())) return 'Hoy';
  if (claveDia(iso) === claveDia(ayer.toISOString())) return 'Ayer';
  return new Intl.DateTimeFormat('es-DO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(d);
}

/**
 * Agrupa los mensajes (ya ordenados) por día, para poder intercalar el
 * separador. Sin él, un mensaje de ayer arriba de los de hoy se lee como un
 * error de orden aunque sea correcto.
 */
export function agruparPorDia(mensajes: Message[]): GrupoDia[] {
  const grupos: GrupoDia[] = [];
  for (const m of mensajes) {
    const iso = horaDeMensaje(m);
    const dia = claveDia(iso);
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.dia === dia) ultimo.mensajes.push(m);
    else grupos.push({ dia, etiqueta: etiquetaDia(iso), mensajes: [m] });
  }
  return grupos;
}

/** Quién escribió un mensaje SALIENTE. */
export interface AutorMensaje {
  tipo: 'agente' | 'panel' | 'movil';
  /** Texto corto para la burbuja. */
  etiqueta: string;
}

/**
 * Autoría de un saliente. `null` para los del cliente: ahí no hace falta
 * aclarar nada, y ensuciaría la burbuja.
 */
export function autorDeMensaje(m: Message): AutorMensaje | null {
  switch (m.role) {
    case 'AGENT':
      return { tipo: 'agente', etiqueta: 'Asistente' };
    case 'HUMAN':
      return { tipo: 'panel', etiqueta: 'Desde el panel' };
    case 'HUMAN_MOBILE':
      return { tipo: 'movil', etiqueta: 'Desde el celular' };
    default:
      return null;
  }
}
